import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Divider, MenuItem, Select, Stack, useTheme } from "@mui/material";
import { QueryInfo, QueryField, socialUniverseField } from "system/query/QueryInfo";
import { socialDatasetId } from "system/query/Query";
import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";
import { Agg } from "system/query/Aggs";
import LeafAggSelector from "components/widgetParts/LeafAggSelector";
import TabbedDialog from "components/dialogs/TabbedDialog";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import SettingsColorPicker from "components/settingsConfig/uiComponents/SettingsColorPicker";
import SettingsCheckbox from "components/settingsConfig/uiComponents/SettingsCheckbox";
import { AutoWidgetQueryBehavior, getSingleOriginalRequestByName, WidgetRequest } from "system/widget/Widget";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";
import QueryWidgetSettingsMissingQueryInfoAlert from "components/widgetParts/queryWidgetComposition/QueryWidgetSettingsMissingQueryInfoAlert";
import { QueryFixedKeywordInput } from "components/queries/queryBuilder/dialogParts/QueryFixedKeywordInput";
import TimeOfDayOfWeekPreview from "./TimeOfDayOfWeekPreview";
import { QueryFieldSelector } from "components/queries/queryBuilder/dialogParts/QueryFieldSelector";
import i18n from "i18next";

export enum KeywordDisplayType {
  COUNT = "count",
  PERCENTAGE = "percentage",
  NET = "net"
}

export type TimeOfDayOfWeekSettingsStateType = {
  customWidgetTitle: string;
  widgetTitle: string;
  widgetTitleAlignment: string;

  hideDayLabels?: boolean;
  hideHourLabels?: boolean;

  minColor?: string;
  midColor?: string;
  maxColor?: string;

  keywordDisplayType?: KeywordDisplayType;
}

export const defaultSettings: TimeOfDayOfWeekSettingsStateType = {
  customWidgetTitle: '',
  widgetTitle: 'default',
  widgetTitleAlignment: 'left',

  hideDayLabels: false,
  hideHourLabels: false,

  keywordDisplayType: KeywordDisplayType.COUNT,
}

const defaultWidgetRequest = {
  name: 'time-of-day-of-week',
  endpoint: 'query/agg',
  queryParams: { },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE
} as WidgetRequest;

export function BuildDefaultTimeOfDayOfWeekRequests(queryInfo: QueryInfo): WidgetRequest[] | undefined {
  let foundTimestampField = queryInfo.fields?.find((field: QueryField) => field.id === "published");
  if (!foundTimestampField) {
    foundTimestampField = queryInfo.fields?.find((field: QueryField) => field.type === "timestamp");
  }

  const foundSocialUniverseField = queryInfo.fields?.find((field: QueryField) => field.id === socialUniverseField.id) || false;

  const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };

  const requestInitBody = { aggs: {} };
  if (foundSocialUniverseField) {
    requestInitBody.aggs = {
      "time_of_day_of_week": {
        op: "time_of_day_of_week",
        field: foundTimestampField?.id || "published",
        _subagg_label: i18n.t("social_universe"),
        aggs: {
          "subagg": {
            op: "social_universe",
            field: socialUniverseField.id
          }
        }
      }
    }
  } else {
    requestInitBody.aggs = {
      "time_of_day_of_week": {
        op: "time_of_day_of_week",
        _subagg_label: i18n.t("records"),
        field: foundTimestampField?.id || "published"
      }
    };
  }
  widgetRequest.requestInit = { body: requestInitBody };
  return [widgetRequest];
}

export default function TimeOfDayOfWeekSettings(props: WidgetSettingsProps): JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const updatedDefaultSettings = {...defaultSettings, color: theme.colors.primary.dark, ...props?.widget?.data?.settings};
  const [settingsState, setSettingsState] = useState<TimeOfDayOfWeekSettingsStateType>(updatedDefaultSettings);
  const settingsOnChange = (updatedState: TimeOfDayOfWeekSettingsStateType) => setSettingsState(updatedState);

  const [localAggs, setLocalAggs] = useState<Agg | undefined | null>(undefined);
  const [fieldLabel, setFieldLabel] = useState<string | undefined>(undefined);

  const [bottomKeyword, setBottomKeyword] = useState<string | undefined>(undefined);
  const [bottomKeywordLabel, setBottomKeywordLabel] = useState<string | undefined>(undefined);

  const [timestampField, setTimestampField] = useState<string | undefined>(undefined);

  useEffect(() => {
    const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'time-of-day-of-week');
    const requestBody = originalRequest?.requestInit?.body || {};

    const mainAggs = requestBody?.aggs?.["time_of_day_of_week"] || undefined;
    const subAggs = mainAggs?.aggs?.["subagg"] || undefined;

    let foundLabel = mainAggs._subagg_label;
    if (!foundLabel) {
      if (subAggs.op === "social_universe") {
        foundLabel = i18n.t("social_universe");
      } else if (subAggs.field) {
        const foundField = props.queryInfo?.fields?.find((field: QueryField) => field.id === subAggs.field);
        foundLabel = foundField?.label || subAggs._field_label || subAggs.field;
      } else {
        foundLabel = i18n.t("records");
      }
    }
    setFieldLabel(foundLabel);

    if (mainAggs?.field) {
      setTimestampField(mainAggs.field);
    } else {
      // get the first timestamp field
      const foundTimestampField = props.queryInfo?.fields?.find((field: QueryField) => field.type === "timestamp");
      setTimestampField(foundTimestampField?.id);
    }

    if (subAggs?.filter && subAggs.filter.length > 1 && subAggs?.labels && subAggs.labels.length > 1) {
      setBottomKeyword(subAggs.filter[1]);
      setBottomKeywordLabel(subAggs.labels[1]);
    }

    setLocalAggs(subAggs || null);
  }, [props.activeWidgetRequests]);

  // check if the data object has the fields needed
  const updateAggs = () => {
    const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'time-of-day-of-week') || defaultWidgetRequest;
    const requestBody = originalRequest?.requestInit?.body || {};
    const mainAggs = requestBody?.aggs?.["time_of_day_of_week"] || undefined;

    if (timestampField) {
      mainAggs.field = timestampField;
    }

    if (settingsState.keywordDisplayType === KeywordDisplayType.NET &&
      localAggs?.filter && bottomKeyword && localAggs?.labels && bottomKeywordLabel) {
      localAggs.filter = [localAggs.filter[0], bottomKeyword];
      localAggs.labels = [localAggs.labels[0], bottomKeywordLabel];
    } else if (settingsState.keywordDisplayType === KeywordDisplayType.NET && (!bottomKeyword || !bottomKeywordLabel)) {
      // We are set to NET mode, but don't have the necessary bottom keyword or label,
      // So reset to percentage mode
      settingsState.keywordDisplayType = KeywordDisplayType.PERCENTAGE;
    }
    if (settingsState.keywordDisplayType !== KeywordDisplayType.NET && localAggs?.filter && localAggs.filter.length > 1 && localAggs?.labels && localAggs.labels.length > 1) {
      // We aren't in NET mode, but have more than one filter/label, so remove the extra ones
      localAggs.filter = [localAggs.filter[0]];
      localAggs.labels = [localAggs.labels[0]];
    }

    const selectedField = props.queryInfo?.fields?.find((field: QueryField) => field.id === localAggs?.field);
    if (selectedField?.type !== "keyword" && selectedField?.type !== "keywords") {
      settingsState.keywordDisplayType = undefined;
      delete localAggs?.filter;
      delete localAggs?.labels;
    }

    if (localAggs) {
      mainAggs.aggs = { ["subagg"]: localAggs };
    } else {
      delete mainAggs?.aggs;
    }

    mainAggs._subagg_label = fieldLabel;
    if (!mainAggs._subagg_label) {
      if (localAggs?.op === "social_universe") {
        mainAggs._subagg_label = i18n.t("social_universe");
      } else if (localAggs?.field) {
        const foundField = props.queryInfo?.fields?.find((field: QueryField) => field.id === localAggs.field);
        mainAggs._subagg_label = foundField?.label || localAggs._field_label || localAggs.field;
      } else {
        mainAggs._subagg_label = i18n.t("records");
      }
    }

    originalRequest.requestInit = { body: requestBody };

    props.saveWidgetDataAndRequests({settings: settingsState}, [originalRequest]).then(() => {
      if (props.widgetRefresh) {
        props.widgetRefresh();
      }
    })
  }

  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);

  const activeField = props.queryInfo?.fields?.find((field: QueryField) => field.id === localAggs?.field);
  const isKeywordField = activeField?.type === "keyword" || activeField?.type === "keywords";

  const isSocialDataset = props.queryInfo?.dataset?.id === socialDatasetId;

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateAggs();
        props.setSettingsDialogIsOpen(false);
      }}
      open={true}
      tabs={[i18n.t("data"), i18n.t("display")]}
      title={i18n.t("widget-types.time-of-day-of-week.settings.title")}>
      <>
        {tabValue === 0 && <>
          {!!props.queryInfo && localAggs !== undefined && (<>
            <SimpleFormLabel>{i18n.t("category")}</SimpleFormLabel>
            <LeafAggSelector
              allowedFieldTypes={["decimal", "integer", "keyword", "keywords"]}
              customFieldLabel={
                settingsState.keywordDisplayType === KeywordDisplayType.NET ?
                i18n.t("widget-types.time-of-day-of-week.settings.top-value") : undefined}
              queryInfo={props.queryInfo}
              aggs={localAggs || undefined}
              setAggsLabel={(label:string) => {
                setFieldLabel(label);
              }}
              setAggs={(newAggs: Agg | null) => {
                const newField = props.queryInfo?.fields?.find((field: QueryField) => field.id === newAggs?.field);
                // Reset the keyword display type if the new field isn't a keyword field
                if (newAggs?.field && newField?.type !== "keyword" && newField?.type !== "keywords") {
                  settingsOnChange({
                    ...settingsState,
                    keywordDisplayType: undefined
                  });
                }
                if (localAggs?.field && newAggs?.field !== localAggs.field) {
                  setBottomKeyword(undefined);
                  setBottomKeywordLabel(undefined);
                }
                setLocalAggs(newAggs || null)
              }}
              ></LeafAggSelector>
              {localAggs?.op === "keyword" && (<>
                <SimpleFormLabel>{i18n.t("widget-types.time-of-day-of-week.settings.keyword-value-type")}</SimpleFormLabel>
                <Select
                  value={settingsState.keywordDisplayType || KeywordDisplayType.COUNT}
                  onChange={(event) => {
                    const newValue = event.target.value as KeywordDisplayType;
                    settingsOnChange({
                      ...settingsState,
                      keywordDisplayType: newValue
                    })
                  }}
                  >
                    <MenuItem value={KeywordDisplayType.COUNT}>{i18n.t("widget-types.time-of-day-of-week.settings.keyword-value-type-options.count")}</MenuItem>
                    <MenuItem value={KeywordDisplayType.PERCENTAGE}>{i18n.t("widget-types.time-of-day-of-week.settings.keyword-value-type-options.percentage")}</MenuItem>
                    <MenuItem value={KeywordDisplayType.NET}>{i18n.t("widget-types.time-of-day-of-week.settings.keyword-value-type-options.net")}</MenuItem>
                </Select>
              </>)}
            {(settingsState.keywordDisplayType === KeywordDisplayType.NET && localAggs?.field && isKeywordField) && (<>
              <SimpleFormLabel>{i18n.t("widget-types.time-of-day-of-week.settings.bottom-value")}</SimpleFormLabel>
              <QueryFixedKeywordInput
                enforceSelection={true}
                excludeOptions={localAggs?.filter?.length ? [localAggs.filter[0]] : undefined}
                queryInfo={props.queryInfo}
                selectedField={props.queryInfo.fields?.find((field: QueryField) => field.id === localAggs.field)}
                value={bottomKeyword}
                setValue={(newValue: string | number | boolean | null | undefined, label?: string | undefined) => {
                  if (newValue !== undefined) {
                    setBottomKeyword(newValue as string);
                    if (label) {
                      setBottomKeywordLabel(label);
                    }
                  }
                }}
              />
            </>)}
            {!isSocialDataset && (<>
              <Divider sx={{ mt: 2 }} />
              <SimpleFormLabel>{i18n.t("widget-types.time-of-day-of-week.settings.time-field")}</SimpleFormLabel>
              <QueryFieldSelector
                allowedFieldTypes={["timestamp"]}
                selectedFieldId={timestampField || ""}
                requireAggSupported={true}
                setSelectedFieldId={(newFieldId:string) => {
                  setTimestampField(newFieldId);
                }}
                fields={props.queryInfo.fields}
              />
            </>)}
          </>)}
          {(!props.queryInfo) &&
            <QueryWidgetSettingsMissingQueryInfoAlert />
          }
        </>}
        {tabValue === 1 &&
          <SplitSettingsWrapper>
            <TimeOfDayOfWeekPreview
              settingsState={settingsState}
              widget={props.widget}
              />
            <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
              <SettingsAccordion label={i18n.t("widget-settings.widget-title")} showBottomDivider={true}>
                <SettingsWidgetTitleSelect
                  keyid="widgetTitle"
                  label={i18n.t("title")}
                  showQueryOption={true}
                  widgetTitleValue={settingsState.widgetTitle} />
                  {settingsState.widgetTitle === 'custom' &&
                    <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                      <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                      <SettingsTextField
                        keyid="customWidgetTitle"
                        label={i18n.t("widget-settings.custom-text")}
                        value={settingsState.customWidgetTitle}
                      />
                    </Stack>}
                {/* Widget Title Alignment */}
                <SettingsToggleButton
                  keyid="widgetTitleAlignment"
                  label={i18n.t("widget-settings.alignment")}
                  optionsList={horizontalAlignmentOptions}
                  value={settingsState.widgetTitleAlignment} />
              </SettingsAccordion>
              <SettingsAccordion label={i18n.t("graph")} showBottomDivider={true}>
                <SettingsColorPicker
                  color={settingsState.maxColor ?? theme.colors.primary.dark}
                  defaultColor={theme.colors.primary.dark}
                  keyid="maxColor"
                  label={
                    (settingsState.keywordDisplayType === KeywordDisplayType.NET) ?
                    i18n.t("widget-types.time-of-day-of-week.settings.top-color") :
                    i18n.t("widget-types.time-of-day-of-week.settings.max-color")
                  } />
                {(settingsState.keywordDisplayType === KeywordDisplayType.NET && localAggs?.field && isKeywordField) && (<>
                  <SettingsColorPicker
                    color={settingsState.midColor ?? theme.colors.primary.main}
                    defaultColor={theme.colors.primary.main}
                    keyid="midColor"
                    label={i18n.t("widget-types.time-of-day-of-week.settings.mid-color")} />
                </>)}
                <SettingsColorPicker
                  color={settingsState.minColor ?? ''}
                  defaultColor={"rgba(255, 255, 255, 0)"}
                  keyid="minColor"
                  label={
                    (settingsState.keywordDisplayType === KeywordDisplayType.NET) ?
                    i18n.t("widget-types.time-of-day-of-week.settings.bottom-color") :
                    i18n.t("widget-types.time-of-day-of-week.settings.min-color")
                  } />
                <SettingsCheckbox
                  checked={settingsState.hideDayLabels}
                  keyid="hideDayLabels"
                  label={i18n.t("widget-types.time-of-day-of-week.settings.hide-day-of-week-labels")} />
                <SettingsCheckbox
                  checked={settingsState.hideHourLabels}
                  keyid="hideHourLabels"
                  label={i18n.t("widget-types.time-of-day-of-week.settings.hide-hour-labels")} />
              </SettingsAccordion>
            </SettingsOptions>
          </SplitSettingsWrapper>}
      </>
    </TabbedDialog>
  )
}
